// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import type { CSSProperties } from "react";
import { useAuth } from "@/lib/auth/auth-provider";
import { cn } from "@/lib/utils";

/**
 * Chain logos are behind a per-user flag while we iterate on the design. Today: enabled only
 * for me (user_id == 1). Once the profile / feature-flag plumbing lands, swap this for a real
 * flag lookup.
 */
export function useChainLogosEnabled(): boolean {
  const { user } = useAuth();
  return user?.userId === 1;
}

interface ChainLogoConfig {
  /** Default rendered height (any CSS length). Defaults to surrounding text height. */
  height?: string;
  /** Vertical nudge to align the mark with the text baseline. */
  translateY?: string;
  /**
   * Set when a dark-mode variant ships alongside the default at
   * `public/logos/chain-<chainName>.dark.webp` (for marks that look wrong on a dark
   * background). When true, `LogoImg` renders both and the `dark` class picks via CSS.
   */
  hasDark?: boolean;
}

/**
 * Registry of chains we ship a logo for (under `public/logos/chain-<chainName>.webp`,
 * plus an optional `chain-<chainName>.dark.webp` when `hasDark` is set).
 * Add a new entry here whenever a logo is dropped into that folder. Per-chain config lets each
 * brand be sized and aligned independently — different marks have very different proportions.
 */
const CHAIN_LOGOS: Readonly<Record<string, ChainLogoConfig>> = {
  asda: { height: "0.75em", translateY: "0em" },
  boots: { height: "1.15em", translateY: "0.20em", hasDark: true },
  focal_systems: { height: "0.85em", translateY: "0em", hasDark: true },
  morrisons: { height: "0.875em", translateY: "0em" },
  wakefern: { height: "1.025em", translateY: "0em", hasDark: true },
};

export function chainHasLogo(chainName: string): boolean {
  return chainName in CHAIN_LOGOS;
}

/**
 * Renders a chain logo `<img>`. When the chain registers `hasDark`, both the default and the
 * `chain-<name>.dark.webp` variant are rendered and the `dark` class on `<html>` (next-themes,
 * class strategy) picks the right one via CSS — no theme hook, no first-paint flash. Chains
 * without a dark variant render the single default image.
 */
function LogoImg({ chainName, className, style }: { chainName: string; className?: string; style?: CSSProperties }) {
  const base = `/logos/chain-${chainName}`;
  if (!CHAIN_LOGOS[chainName]?.hasDark) {
    return <img src={`${base}.webp`} alt="" loading="lazy" className={className} style={style} />;
  }
  return (
    <>
      <img src={`${base}.webp`} alt="" loading="lazy" className={cn(className, "dark:hidden")} style={style} />
      <img
        src={`${base}.dark.webp`}
        alt=""
        loading="lazy"
        className={cn(className, "hidden dark:block")}
        style={style}
      />
    </>
  );
}

interface ChainLogoProps {
  chainName: string;
  size: number;
  className?: string;
}

/**
 * Renders the chain's logo at an explicit pixel height. The per-chain inline tuning
 * (em-based height + baseline translate) is reserved for the in-text use inside `ChainLink`.
 */
export function ChainLogo({ chainName, size, className }: ChainLogoProps) {
  if (!CHAIN_LOGOS[chainName]) return null;
  return (
    <LogoImg
      chainName={chainName}
      className={cn("shrink-0 max-w-none", className)}
      style={{ height: size, width: "auto" }}
    />
  );
}

interface ChainLinkProps {
  chainName: string;
  legibleName?: string;
  className?: string;
  showIcon?: boolean;
}

/**
 * Linked chain reference: small logo (if registered) + legible name, pointing to
 * `/chains/$chainName`. Drop-in for the per-cell `<Link>` we used to render in data tables.
 * The embedded logo uses the per-chain inline tuning (em-based height + baseline translate);
 * for any non-inline use, call `<ChainLogo size={...} />` directly with an explicit pixel size.
 */
export function ChainLink({ chainName, legibleName, className, showIcon = true }: ChainLinkProps) {
  const logosEnabled = useChainLogosEnabled();
  const config = CHAIN_LOGOS[chainName];
  const hasLogo = logosEnabled && config != null;
  return (
    <Link
      to="/chains/$chainName"
      params={{ chainName }}
      className={cn(
        "inline-flex items-center gap-1.5 text-primary hover:underline",
        // Logo-only links lose the trailing text whitespace that gave cells their right-side
        // breathing room — pad the right edge to compensate.
        hasLogo && "pr-1.5",
        className,
      )}
      aria-label={hasLogo ? legibleName || chainName : undefined}
    >
      {hasLogo ? (
        // max-w-none defeats Tailwind preflight's `img { max-width: 100% }`, which would
        // otherwise cap the logo's width to a narrow cell and squash its aspect ratio.
        <LogoImg
          chainName={chainName}
          className="shrink-0 max-w-none"
          style={{
            height: config.height ?? "1em",
            width: "auto",
            transform: config.translateY ? `translateY(${config.translateY})` : undefined,
          }}
        />
      ) : (
        <>
          {showIcon && <Building2 className="size-3.5 shrink-0" aria-hidden="true" />}
          <span>{legibleName || chainName}</span>
        </>
      )}
    </Link>
  );
}
